/**
 * 
 * @param JSONString - The JSON string to validate.
 * @returns The parsed JSON object if the JSONString is valid, otherwise null.
 */
const JSONValidator = (JSONString: string | null): any => {
    if (!JSONString) return null;
    try {
        const JSONObject = JSON.parse(JSONString);
        return JSONObject;
    } catch (error) {
        return null;
    }
}

/**
 * 
 * @param validateeObject - The object to validate.
 * @param validatorObject - The object containing validation rules.
 * @returns true if validateeObject matches validatorObject, otherwise false.
 */
const ObjectValidator = (validateeObject: { [key: string]: any }, validatorObject: { [key: string]: any }): boolean | null => {
    const allValidateKeys = Object.keys(validatorObject);
    const allValidateeKeys = Object.keys(validateeObject);

    if (allValidateKeys.length !== allValidateeKeys.length)
        return null;

    let isValidObject = true;
    for (let i = 0; i < allValidateKeys.length; i++) {
        const validateKey = allValidateKeys[i];
        if (validateeObject[validateKey] && validateeObject[validateKey].length > 0 && typeof validateeObject[validateKey] === typeof validatorObject[validateKey])
            continue;
        isValidObject = false;
        break;
    }

    return isValidObject;
}

const emailValidator = (email: string) => {
    return /^([\w-]+(?:\.[\w-]+)*)@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$/.test(email)
}

function validateHttps(url: string) {
    if (!url.startsWith("https://")) {
        url = "https://" + url;
    }
    return url;
}


function luhnCheck(cardNumber: string) {
    const digits = cardNumber.split('').map(Number);
    let sum = 0;
    let shouldDouble = false;

    for (let i = digits.length - 1; i >= 0; i--) {
        let digit = digits[i];

        if (shouldDouble) {
            digit *= 2;
            if (digit > 9) {
                digit -= 9;
            }
        }

        sum += digit;
        shouldDouble = !shouldDouble;
    }

    return sum % 10 === 0;
}

function getCardType(cardNumber: string) {
    // Regular expressions to detect card type
    const visaRegex = /^4[0-9]{12}(?:[0-9]{3})?$/;
    const mastercardRegex = /^5[1-5][0-9]{14}$/;
    const amexRegex = /^3[47][0-9]{13}$/;
    const discoverRegex = /^6(?:011|5[0-9]{2})[0-9]{12}$/;

    if (visaRegex.test(cardNumber)) return "Visa";
    if (mastercardRegex.test(cardNumber)) return "MasterCard";
    if (amexRegex.test(cardNumber)) return "American Express (Amex)";
    if (discoverRegex.test(cardNumber)) return "Discover";
    return "Unknown";
}

function validateCard(cardNumber: string) {

    const isValid = luhnCheck(cardNumber);
    const cardType = getCardType(cardNumber);

    return { isValid, cardType }
}

const addSpacesToCreditCard = (inputElement: HTMLInputElement) => {
    // Get the current value and caret position
    const rawValue = inputElement.value;
    const caretPosition = inputElement.selectionStart ?? rawValue.length;

    // Extract only the digits
    const digits = rawValue.split('').filter((char) => /\d/.test(char)).join('')?.substring(0, 16);

    // Format the digits with spaces
    const formattedValue = digits.replace(/(.{4})/g, '$1 ').trim();

    // Calculate the new caret position
    let adjustedPosition = caretPosition;
    let digitIndex = 0;

    // Traverse the raw value up to the caret position
    for (let i = 0; i < caretPosition; i++) {
        if (/\d/.test(rawValue[i])) {
            digitIndex++;
        }
    }

    // Traverse the formatted value to find the equivalent caret position
    adjustedPosition = 0;
    let digitCount = 0;
    for (let i = 0; i < formattedValue.length; i++) {
        if (/\d/.test(formattedValue[i])) {
            digitCount++;
        }
        if (digitCount === digitIndex) {
            adjustedPosition = i + 1;
            break;
        }
    }

    // Update the input value and set the caret position
    setTimeout(() => {

        inputElement.setSelectionRange(adjustedPosition, adjustedPosition);
    })
    return formattedValue
};

const addSpacesToCreditCardDate = (inputElement: HTMLInputElement) => {
    // Get the current value and caret position
    const rawValue = inputElement.value;
    const caretPosition = inputElement.selectionStart ?? rawValue.length;

    // Extract only digits
    const digits = rawValue.split('').filter((char) => /\d/.test(char)).join('');

    // Format the digits into MM/YY
    let formattedValue = digits;
    if (digits.length > 2) {
        formattedValue = `${digits.slice(0, 2)}/${digits.slice(2, 4)}`;
    }

    // Calculate the new caret position
    let adjustedPosition = caretPosition;
    let digitIndex = 0;

    // Traverse the raw value up to the caret position
    for (let i = 0; i < caretPosition; i++) {
        if (/\d/.test(rawValue[i])) {
            digitIndex++;
        }
    }

    // Traverse the formatted value to find the equivalent caret position
    adjustedPosition = 0;
    let digitCount = 0;
    for (let i = 0; i < formattedValue.length; i++) {
        if (/\d/.test(formattedValue[i])) {
            digitCount++;
        }
        if (digitCount === digitIndex) {
            adjustedPosition = i + 1;
            break;
        }
    }

    // Adjust position for the slash (`/`)
    if (digitIndex > 2 && formattedValue[adjustedPosition - 1] === '/') {
        adjustedPosition++;
    }

    // Update the input value and set the caret position
    setTimeout(() => {
        inputElement.setSelectionRange(adjustedPosition, adjustedPosition);

    })
    return formattedValue
};

export { JSONValidator, ObjectValidator, emailValidator, validateHttps, validateCard, addSpacesToCreditCard, addSpacesToCreditCardDate }